/**
 * Datos de la Atención form
 */
import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar, Upload, Trash2, FileText, ExternalLink, Loader2 } from 'lucide-react';

const AtencionDatosForm = ({ 
  datosAtencion, 
  onDatosChange,
  onActaUpload,
  onActaDelete,
  actaInputRef,
  inspeccionId,
  inspeccionActaEstado,
  atencionId,
  sendingEmail = false,
  readOnly = false
}) => {
  // Generate acta URL dynamically if we have inspeccion_id
  const actaInspeccionUrl = datosAtencion.actaInspeccionUrl || 
    (inspeccionId ? `${process.env.REACT_APP_BACKEND_URL}/api/inspecciones/${inspeccionId}/acta-view` : null);
  
  // Check if atencion email/whatsapp was sent (any of these states means acta was sent)
  const actaWasSent = ['enviado_correo', 'aprobado_correo', 'rechazado_correo', 'enviado_whatsapp', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(datosAtencion.actaEstado);
  
  // Generate acta atencion view URL dynamically
  const actaAtencionViewUrl = atencionId 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/atenciones/${atencionId}/acta-view`
    : null;
  
  return (
    <Card className="border-[#E8E8E7]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Calendar size={18} className="text-[#9d8272]" />
          Datos de la Atención
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Fecha *</Label>
            <Input
              type="date"
              value={datosAtencion.fecha}
              onChange={(e) => onDatosChange({...datosAtencion, fecha: e.target.value})}
              className={`border-[#c5b8a2] text-sm ${readOnly ? 'bg-gray-50' : ''}`}
              readOnly={readOnly}
              data-testid="atencion-fecha"
            />
          </div>
          <div>
            <Label className="text-xs">Hora Inicio *</Label>
            <Input
              type="time"
              value={datosAtencion.horaInicio}
              onChange={(e) => onDatosChange({...datosAtencion, horaInicio: e.target.value})}
              className={`border-[#c5b8a2] text-sm ${readOnly ? 'bg-gray-50' : ''}`}
              readOnly={readOnly}
              data-testid="atencion-hora-inicio"
            />
          </div>
          <div>
            <Label className="text-xs">Hora Fin</Label>
            <Input
              type="time"
              value={datosAtencion.horaFin}
              readOnly
              className="border-[#c5b8a2] text-sm bg-gray-50"
              placeholder="Auto al terminar"
              data-testid="atencion-hora-fin"
            />
          </div>
          <div>
            <Label className="text-xs">Acta de Inspección</Label>
            {actaInspeccionUrl ? (
              <div className="flex flex-col gap-1 mt-1">
                <a
                  href={actaInspeccionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                  data-testid="acta-inspeccion-link"
                >
                  <FileText size={14} />
                  <span>Ver acta inspección</span>
                  <ExternalLink size={12} />
                </a>
                {inspeccionActaEstado && (
                  <Badge 
                    className={`text-[10px] w-fit ${
                      ['aprobado_correo', 'aprobado_whatsapp'].includes(inspeccionActaEstado) ? 'bg-green-100 text-green-800 border-green-300' :
                      ['rechazado_correo', 'rechazado_whatsapp'].includes(inspeccionActaEstado) ? 'bg-red-100 text-red-800 border-red-300' :
                      'bg-blue-100 text-blue-800 border-blue-300'
                    }`} 
                    variant="outline"
                  >
                    {inspeccionActaEstado === 'enviado_correo' ? 'Enviado por correo' : 
                     inspeccionActaEstado === 'enviado_whatsapp' ? 'Enviado por WhatsApp' :
                     inspeccionActaEstado === 'aprobado_correo' ? 'Aprobado por correo' :
                     inspeccionActaEstado === 'aprobado_whatsapp' ? 'Aprobado por WhatsApp' :
                     inspeccionActaEstado === 'rechazado_correo' ? 'Rechazado por correo' :
                     inspeccionActaEstado === 'rechazado_whatsapp' ? 'Rechazado por WhatsApp' :
                     inspeccionActaEstado}
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-2">Sin acta de inspección</p>
            )}
          </div>
        </div>
        
        {/* Acta de Atención */}
        <div>
          <Label className="text-xs">Acta de Atención</Label>
          <input
            ref={actaInputRef}
            type="file"
            accept="application/pdf"
            onChange={onActaUpload}
            className="hidden"
          />
          
          {/* Show spinner while sending email */}
          {sendingEmail ? (
            <div className="flex items-center gap-2 mt-1 text-blue-600">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Enviando correo...</span>
            </div>
          ) : actaWasSent && actaAtencionViewUrl ? (
            // Show link to acta when email was sent
            <div className="flex items-center gap-2 mt-1">
              <a
                href={actaAtencionViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                data-testid="acta-atencion-link"
              >
                <FileText size={14} />
                <span>Ver acta de atención</span>
                <ExternalLink size={12} />
              </a>
              <Badge 
                className={`text-[10px] ${
                  ['aprobado_correo', 'aprobado_whatsapp'].includes(datosAtencion.actaEstado) ? 'bg-green-100 text-green-800 border-green-300' :
                  ['rechazado_correo', 'rechazado_whatsapp'].includes(datosAtencion.actaEstado) ? 'bg-red-100 text-red-800 border-red-300' :
                  datosAtencion.actaEstado === 'enviado_whatsapp' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                  'bg-blue-100 text-blue-800 border-blue-300'
                }`} 
                variant="outline"
              >
                {datosAtencion.actaEstado === 'enviado_correo' ? 'Enviado por correo' : 
                 datosAtencion.actaEstado === 'aprobado_correo' ? 'Aprobado por correo' :
                 datosAtencion.actaEstado === 'rechazado_correo' ? 'Rechazado por correo' :
                 datosAtencion.actaEstado === 'enviado_whatsapp' ? 'Enviado por WhatsApp' :
                 datosAtencion.actaEstado === 'aprobado_whatsapp' ? 'Aprobado WhatsApp' :
                 datosAtencion.actaEstado === 'rechazado_whatsapp' ? 'Rechazado WhatsApp' :
                 datosAtencion.actaEstado}
              </Badge>
            </div>
          ) : datosAtencion.actaAtencionUrl ? (
            // Show link when acta was uploaded manually
            <div className="flex items-center gap-2 mt-1">
              <a
                href={datosAtencion.actaAtencionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                data-testid="acta-atencion-link"
              >
                <FileText size={14} />
                <span>Acta de atención</span>
                <ExternalLink size={12} />
              </a>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onActaDelete}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                  title="Eliminar acta"
                  data-testid="acta-atencion-delete-btn"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          ) : !readOnly ? (
            // Show upload button when no acta and email not sent (only in edit mode)
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => actaInputRef.current?.click()}
              className="mt-1 border-[#c5b8a2] text-[#9d8272]"
              data-testid="acta-atencion-upload-btn"
            >
              <Upload size={14} className="mr-1" />
              Adjuntar
            </Button>
          ) : (
            <span className="text-xs text-gray-500 mt-1 block">Sin acta</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AtencionDatosForm;
